// @spec authentication-google-oauth authentication-reauth
//
// Canonical Google OAuth scope list. Lives in @fpo3/famfam-core so both server-side
// NextAuth wiring and client-side reauth/redirect helpers can import the
// same source of truth — without pulling `server-only` into client bundles.

const GOOGLEAPIS_AUTH_PREFIX = 'https://www.googleapis.com/auth/';

const OIDC_SHORT_SCOPES: ReadonlySet<string> = new Set(['openid', 'email', 'profile']);

// Google's OAuth server expands the OIDC short scopes `email` and `profile`
// into these full-URL forms when persisting the granted scope on the token
// (and therefore on `Account.scope`). The reverse map lets equivalence checks
// against GOOGLE_OAUTH_SCOPES (which carries the short forms) succeed
// regardless of which form the input uses. `openid` is granted back verbatim,
// so it has no entry here.
const OIDC_URL_TO_SHORT: ReadonlyMap<string, string> = new Map([
  ['https://www.googleapis.com/auth/userinfo.email', 'email'],
  ['https://www.googleapis.com/auth/userinfo.profile', 'profile'],
]);

/**
 * Base Google OAuth scopes requested at sign-in. Note: `calendar.acls` was
 * added after initial launch — users who signed in before it was added will
 * hit `ACCESS_TOKEN_SCOPE_INSUFFICIENT` on calendar-sharing ACL calls and
 * must reconnect to pick up the new scope (see the `reauth-scope-insufficient`
 * behavioral rule). Adding a scope here flows through every OAuth flow
 * (initial sign-in, reauth, link).
 */
export const GOOGLE_OAUTH_SCOPES: readonly string[] = [
  'openid',
  'email',
  'profile',
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/calendar.events',
  // Needed for the people-settings "Share" button to write ACL rules on
  // calendars the user owns.
  'https://www.googleapis.com/auth/calendar.acls',
  'https://www.googleapis.com/auth/contacts.readonly',
  'https://www.googleapis.com/auth/contacts.other.readonly',
];

/**
 * Base authorization params for every Google OAuth request. Centralised here
 * so the NextAuth provider config and any manual OAuth URL construction share
 * one source of truth.
 *
 * `include_granted_scopes: 'true'` is critical: without it, a re-authentication
 * that requests only the base scopes silently drops any incrementally-granted
 * scope (e.g. `gmail.readonly`). With it, Google issues a token covering the
 * union of the requested scopes and all previously-granted scopes for this
 * user+client, preventing scope shrinkage.
 */
export const GOOGLE_OAUTH_AUTHORIZATION_PARAMS = {
  access_type: 'offline',
  prompt: 'consent',
  include_granted_scopes: 'true',
} as const;

/**
 * Trim and expand a short-form googleapis scope (e.g. `gmail.readonly`) to
 * its full URL form. OIDC short scopes (`openid` / `email` / `profile`) and
 * any value already containing `://` pass through unchanged. Returns `null`
 * for empty or whitespace-only input so callers can drop blank entries
 * instead of forwarding the bare googleapis prefix to Google (which would
 * round-trip as `invalid_scope`).
 */
function normalizeScope(scope: string): string | null {
  const trimmed = scope.trim();
  if (trimmed === '') return null;
  if (trimmed.includes('://')) return trimmed;
  if (OIDC_SHORT_SCOPES.has(trimmed)) return trimmed;
  return `${GOOGLEAPIS_AUTH_PREFIX}${trimmed}`;
}

/**
 * Canonical full URL of the Gmail read-only scope. Importable in any layer
 * that needs to compare the user's granted scope string against the
 * email-intelligence opt-in requirement.
 */
export const GMAIL_READONLY_SCOPE = 'https://www.googleapis.com/auth/gmail.readonly';

/**
 * Non-base scopes the reauth flow is allowed to forward via
 * `additionalScopes`. The `cross-env-login` endpoint enforces this same
 * allowlist on `scope` arrival; filtering here keeps any
 * historically-granted-but-no-longer-allowed token out of the wire shape
 * sent to clients (FPO-997 #2 — without filtering, the banner could 400
 * the cross-env-login on click).
 */
export const REAUTH_FORWARDABLE_SCOPES: ReadonlySet<string> = new Set([GMAIL_READONLY_SCOPE]);

/**
 * Whether the (whitespace-separated) `scope` value granted to an Account
 * row contains `requiredScope`. Both inputs are run through
 * {@link normalizeScope} so callers can pass either the canonical
 * `https://www.googleapis.com/auth/...` URL form or the short alias
 * (`gmail.readonly`) on either side of the comparison — symmetric with
 * {@link computeGoogleScopes}, which expands short forms before forwarding
 * them to Google. Empty / whitespace-only tokens are dropped.
 */
export function accountHasScope(scope: string | null | undefined, requiredScope: string): boolean {
  if (scope == null) return false;
  const normalizedRequired = normalizeScope(requiredScope);
  if (normalizedRequired == null) return false;
  return scope
    .split(/\s+/)
    .map(normalizeScope)
    .some((s) => s === normalizedRequired);
}

/**
 * Collapse a normalized scope to its canonical equivalence-class form. Maps
 * Google-expanded OIDC URLs back to their short forms so set-membership
 * checks against {@link GOOGLE_OAUTH_SCOPES} (which carries the short forms)
 * succeed. Returns `null` for blank input.
 */
export function canonicalizeScope(scope: string): string | null {
  const normalized = normalizeScope(scope);
  if (normalized == null) return null;
  return OIDC_URL_TO_SHORT.get(normalized) ?? normalized;
}

/**
 * Extract scopes granted on `Account.scope` that are NOT part of
 * {@link GOOGLE_OAUTH_SCOPES}. Used by reauth flows so the OAuth round-trip
 * preserves previously-granted incremental-consent scopes (e.g.
 * `gmail.readonly` for the email-intelligence opt-in) instead of silently
 * shrinking `Account.scope` to the base list.
 *
 * The bug this guards against: NextAuth's `signIn('google')` and the
 * cross-env-login route default to {@link GOOGLE_OAUTH_SCOPES} when no
 * explicit `scope` param is present. A reauth caller that omits
 * `additionalScopes` therefore re-issues a token with only the base scopes,
 * Google grants only those, and the post-OAuth persist overwrites
 * `Account.scope` accordingly. Feeding the existing extras back into the
 * OAuth round-trip preserves them.
 *
 * Returns tokens directly compatible with {@link computeGoogleScopes} and
 * `additionalScopes` callers (full URL form for googleapis scopes, short form
 * for OIDC short scopes; deduped, blanks dropped).
 */
// Built once at module load; GOOGLE_OAUTH_SCOPES is immutable so this is safe
// to cache as a module-level constant rather than reconstructing on every call.
const BASE_SCOPE_CANONICAL: ReadonlySet<string> = (() => {
  const s = new Set<string>();
  for (const scope of GOOGLE_OAUTH_SCOPES) {
    const c = canonicalizeScope(scope);
    if (c != null) s.add(c);
  }
  return s;
})();

export function extractNonBaseScopes(scope: string | null | undefined): string[] {
  if (scope == null) return [];
  const out: string[] = [];
  const seenCanonical = new Set<string>();
  for (const raw of scope.split(/\s+/)) {
    const normalized = normalizeScope(raw);
    if (normalized == null) continue;
    const canonical = OIDC_URL_TO_SHORT.get(normalized) ?? normalized;
    if (BASE_SCOPE_CANONICAL.has(canonical)) continue;
    if (seenCanonical.has(canonical)) continue;
    seenCanonical.add(canonical);
    out.push(normalized);
  }
  return out;
}

/**
 * Canonicalized form of `REAUTH_FORWARDABLE_SCOPES`, built once at module
 * load. Used by {@link filterReauthForwardable} to compare incoming tokens
 * (which may be in OIDC URL form or short alias) by canonical equivalence
 * class.
 */
const REAUTH_FORWARDABLE_CANONICAL: ReadonlySet<string> = (() => {
  const s = new Set<string>();
  for (const scope of REAUTH_FORWARDABLE_SCOPES) {
    const c = canonicalizeScope(scope);
    if (c != null) s.add(c);
  }
  return s;
})();

/**
 * Drop any non-base scope not on `REAUTH_FORWARDABLE_SCOPES`. Tokens are
 * compared in canonical form so OIDC-URL forms and short equivalents
 * collapse to the same identity. Callers typically build `extras` from
 * {@link extractNonBaseScopes}.
 *
 * Centralized here so both `lib/server/google-reauth.ts` (`userNeedsGoogleReauth`)
 * and `lib/server/service-handlers/family-config.ts` (`getLinkedAccounts`)
 * apply the same allowlist before exposing extras to reconnect callers —
 * preventing a historically-granted-but-now-disallowed scope from tripping
 * the cross-env-login 400 (FPO-997 #2).
 */
export function filterReauthForwardable(extras: readonly string[]): string[] {
  return extras.filter((t) => {
    const c = canonicalizeScope(t);
    return c != null && REAUTH_FORWARDABLE_CANONICAL.has(c);
  });
}

/**
 * Merge an existing persisted scope string with an incoming scope string,
 * preserving non-base scopes from the existing value that are absent from
 * the incoming value. Prevents silent scope shrinkage during OAuth
 * round-trips that only request base scopes (login flow, save-toast
 * reconnect, or any caller that forgets to forward extras).
 *
 * Returns `null` only when both inputs are null/undefined.
 */
export function mergePersistedScope(
  existing: string | null | undefined,
  incoming: string | null | undefined
): string | null {
  if (incoming == null && existing == null) return null;
  if (incoming == null) return existing!;
  if (existing == null) return incoming;

  const existingExtras = extractNonBaseScopes(existing);
  if (existingExtras.length === 0) return incoming;

  const incomingTokens = new Set<string>();
  for (const raw of incoming.split(/\s+/)) {
    const normalized = normalizeScope(raw);
    if (normalized != null) incomingTokens.add(normalized);
  }
  const missing = existingExtras.filter((e) => !incomingTokens.has(e));
  if (missing.length === 0) return incoming;

  return `${incoming} ${missing.join(' ')}`;
}

/**
 * Compute the forwardable extra scopes for a reauth/reconnect flow.
 * Starts from the Account's persisted scope, filters to the forwardable
 * allowlist, then injects capability-required scopes (e.g. `gmail.readonly`
 * for enabled EmailAccounts) that may have been silently lost during a
 * prior OAuth round-trip (FPO-1668).
 *
 * Both `userNeedsGoogleReauth` and `getLinkedAccounts` call this so
 * every reconnect entry point requests the same scope set.
 */
export function computeReauthExtraScopes(
  accountScope: string | null | undefined,
  options?: { emailAccountEnabled?: boolean }
): string[] {
  const extras = filterReauthForwardable(extractNonBaseScopes(accountScope));
  if (options?.emailAccountEnabled && !extras.includes(GMAIL_READONLY_SCOPE)) {
    extras.push(GMAIL_READONLY_SCOPE);
  }
  return extras;
}

/**
 * Compose the full OAuth scope list for a Google sign-in or reauth flow.
 * Returns the base list followed by any extra scopes (trimmed, deduped,
 * short-form expanded; blank/whitespace-only entries are dropped). Order is
 * stable so consent-screen diffs are predictable.
 */
export function computeGoogleScopes(extraScopes: readonly string[]): readonly string[] {
  const out: string[] = [...GOOGLE_OAUTH_SCOPES];
  const seen = new Set<string>(out);
  for (const raw of extraScopes) {
    const normalized = normalizeScope(raw);
    if (normalized != null && !seen.has(normalized)) {
      seen.add(normalized);
      out.push(normalized);
    }
  }
  return out;
}
